import { predictLength } from 'client-zip';
import type { DownloadPart, ManifestFile, SinkKind } from './types';

/**
 * Splits a download into independently-valid ZIP files ("parts") when a single
 * archive would be too large for the chosen sink.
 *
 * Parts are independent archives, not spanned `.z01` volumes — same as Google
 * Drive. Every part opens with a stock unzip tool; a spanned set would need
 * 7-Zip and would be useless if one part failed.
 */

/** How big one part may get, per sink. A streaming sink is bounded by disk, a
 *  Blob sink by RAM — which is why iOS gets the smallest budget. */
const TARGET_BYTES: Record<SinkKind, number> = {
  // Staged on disk, so bounded by free space rather than RAM.
  opfs: 4 * 1024 ** 3,
  serviceworker: 4 * 1024 ** 3,
  blob: 1 * 1024 ** 3,
};

/** Blob sink on a phone — iOS Safari discards large Blobs and kills the tab. */
const MOBILE_BLOB_TARGET = 400 * 1024 ** 2;

/** Above this the UI must warn before starting; a phone will struggle. */
export const MOBILE_WARN_BYTES = 2 * 1024 ** 3;
/** Above this we refuse on a Blob sink — the tab would almost certainly crash. */
export const MOBILE_REFUSE_BYTES = 5 * 1024 ** 3;

export function isMobileLike(): boolean {
  if (typeof navigator === 'undefined') return false;
  // Coarse pointer + small viewport is a better signal than UA sniffing, and
  // correctly catches iPadOS pretending to be a desktop.
  const coarse = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
  const small = typeof window !== 'undefined' && Math.min(window.screen.width, window.screen.height) < 820;
  return coarse && small;
}

export function targetBytesFor(sink: SinkKind, mobile = isMobileLike()): number {
  if (sink === 'blob' && mobile) return MOBILE_BLOB_TARGET;
  return TARGET_BYTES[sink];
}

/** Entry name inside the archive: everything lives under one album folder. */
export function entryName(folderName: string, file: ManifestFile): string {
  return `${folderName}/${file.name}`;
}

/**
 * Exact size of the ZIP that would be produced for these files — headers,
 * central directory and ZIP64 records included, not a percentage guess.
 */
export function predictPartBytes(folderName: string, files: ManifestFile[]): number {
  return Number(
    predictLength(files.map((f) => ({ name: entryName(folderName, f), size: f.bytes }))),
  );
}

/**
 * `Album.zip` when it fits in one part, `Album (2 of 4).zip` when it doesn't.
 * The " (n of m)" form sorts correctly and reads the way Drive's does.
 */
export function partName(folderName: string, index: number, count: number): string {
  return count === 1 ? `${folderName}.zip` : `${folderName} (${index + 1} of ${count}).zip`;
}

/**
 * Greedy sequential fill, preserving manifest order — photos stay chronological
 * within and across parts, which is what people expect from an event album.
 *
 * A single file larger than the target gets a part to itself rather than being
 * split, since we don't produce spanned archives.
 */
export function planParts(
  files: ManifestFile[],
  opts: { folderName: string; targetBytes: number },
): DownloadPart[] {
  const { folderName, targetBytes } = opts;
  if (files.length === 0) return [];

  // One archive if the whole thing fits — the common case, and it avoids
  // showing multi-part UI to someone downloading 30 photos.
  const wholeBytes = predictPartBytes(folderName, files);
  if (wholeBytes <= targetBytes) {
    return [{ index: 0, name: partName(folderName, 0, 1), files, bytes: wholeBytes }];
  }

  const groups: ManifestFile[][] = [];
  let current: ManifestFile[] = [];

  for (const file of files) {
    if (current.length === 0) {
      current.push(file);
      continue;
    }
    // Re-predicting per file is O(n) per step but exact; n is bounded by the
    // photo count and this runs once, before any network work.
    if (predictPartBytes(folderName, [...current, file]) > targetBytes) {
      groups.push(current);
      current = [file];
    } else {
      current.push(file);
    }
  }
  if (current.length > 0) groups.push(current);

  return groups.map((groupFiles, i) => ({
    index: i,
    name: partName(folderName, i, groups.length),
    files: groupFiles,
    bytes: predictPartBytes(folderName, groupFiles),
  }));
}
